/**
 * LocalStorage adapter for IGameRepository (Wave 2 persistence context)
 */
import type { GameSaveSnapshot, IGameRepository } from '../../domain/ports/IGameRepository';
import type { GameStatus } from '../../../types';
import {
  recoverSaveData,
  type SaveData,
} from '../../../utils/saveLoadValidator';
import { logCritical } from '../../../utils/errorHandler';

import { SAVE_KEY, SAVE_VERSION } from '../../infrastructure/config/persistence';

export { SAVE_KEY };

export interface StorageLike {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
  /** Optional key enumeration for backup discovery (localStorage, test doubles). */
  keys?(): IterableIterator<string>;
}

const defaultStorage = (): StorageLike => {
  if (typeof localStorage !== 'undefined') {
    return localStorage;
  }
  throw new Error('localStorage is not available');
};

const toGameStatus = (value: unknown): GameStatus => {
  if (value === 'playing' || value === 'won' || value === 'lost' || value === 'scenario_won') {
    return value;
  }
  return 'playing';
};

const toSnapshot = (
  data: SaveData,
  raw: Record<string, unknown>,
  loadRecoveryReason?: string,
): GameSaveSnapshot => ({
  version: typeof raw.version === 'string' ? raw.version : data.version ?? SAVE_VERSION,
  timestamp: typeof raw.timestamp === 'number' ? raw.timestamp : data.timestamp,
  grid: data.grid,
  stats: data.stats,
  research: data.research,
  milestones: Array.isArray(raw.milestones) ? raw.milestones : [],
  gameStatus: toGameStatus(raw.gameStatus ?? data.gameStatus),
  newsFeed: Array.isArray(raw.newsFeed)
    ? raw.newsFeed
    : Array.isArray(data.newsFeed)
      ? data.newsFeed
      : [],
  currentGoal: (raw.currentGoal ?? data.currentGoal ?? null) as GameSaveSnapshot['currentGoal'],
  savedAt: typeof raw.savedAt === 'string' ? raw.savedAt : undefined,
  loadRecoveryReason,
});

export class LocalStorageGameRepository implements IGameRepository {
  constructor(
    private readonly storage: StorageLike = defaultStorage(),
    private readonly saveKey: string = SAVE_KEY,
  ) {}

  private createBackup(): void {
    try {
      const data = this.storage.getItem(this.saveKey);
      if (!data) return;
      this.storage.setItem(`${this.saveKey}_backup_${Date.now()}`, data);
    } catch {
      // Non-fatal — load proceeds without backup
    }
  }

  private listBackupKeys(): string[] {
    const prefix = `${this.saveKey}_backup_`;
    const keys: string[] = [];

    if (this.storage.keys) {
      for (const key of this.storage.keys()) {
        if (key.startsWith(prefix)) {
          keys.push(key);
        }
      }
    } else {
      const storageApi = this.storage as Storage;
      if (
        typeof storageApi.length === 'number' &&
        typeof storageApi.key === 'function'
      ) {
        for (let i = 0; i < storageApi.length; i++) {
          const key = storageApi.key(i);
          if (key?.startsWith(prefix)) {
            keys.push(key);
          }
        }
      }
    }

    return keys.sort().reverse();
  }

  private parseSaveRaw(raw: string): GameSaveSnapshot | null {
    try {
      const parsed = JSON.parse(raw) as Record<string, unknown>;
      const recovery = recoverSaveData(parsed);

      if (recovery.recovered) {
        logCritical('Corrupt save recovered during load', {
          source: 'LocalStorageGameRepository',
          recoveryReason: recovery.recoveryReason,
          errors: recovery.validation.errors,
          warnings: recovery.validation.warnings,
        });
      }

      return toSnapshot(
        recovery.data,
        { ...parsed, ...recovery.data },
        recovery.recoveryReason ?? undefined,
      );
    } catch {
      return null;
    }
  }

  async save(snapshot: GameSaveSnapshot): Promise<void> {
    const payload = {
      stats: snapshot.stats,
      research: snapshot.research,
      grid: snapshot.grid,
      milestones: snapshot.milestones,
      gameStatus: snapshot.gameStatus,
      newsFeed: snapshot.newsFeed,
      currentGoal: snapshot.currentGoal,
      version: snapshot.version ?? SAVE_VERSION,
      timestamp: snapshot.timestamp ?? Date.now(),
      savedAt: snapshot.savedAt ?? new Date().toISOString(),
    };
    this.storage.setItem(this.saveKey, JSON.stringify(payload));
  }

  async load(): Promise<GameSaveSnapshot | null> {
    try {
      const raw = this.storage.getItem(this.saveKey);
      if (!raw) {
        return null;
      }

      this.createBackup();

      const snapshot = this.parseSaveRaw(raw);
      if (snapshot) {
        return snapshot;
      }

      for (const backupKey of this.listBackupKeys()) {
        const backupRaw = this.storage.getItem(backupKey);
        if (!backupRaw) {
          continue;
        }

        const backupSnapshot = this.parseSaveRaw(backupRaw);
        if (backupSnapshot) {
          logCritical('Save load recovered from backup', {
            source: 'LocalStorageGameRepository',
            backupKey,
          });
          return backupSnapshot;
        }
      }

      logCritical('Save load failed', {
        source: 'LocalStorageGameRepository',
        error: 'Primary save and all backups failed to parse',
      });
      return null;
    } catch (error: unknown) {
      logCritical('Save load failed', {
        source: 'LocalStorageGameRepository',
        error: error instanceof Error ? error.message : String(error),
      });
      return null;
    }
  }

  hasSave(): boolean {
    return !!this.storage.getItem(this.saveKey);
  }

  async delete(): Promise<void> {
    this.storage.removeItem(this.saveKey);
  }
}